<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { TtLocationFragment, StoreStockStatusFragment, StoreStockStatusStoreFragment } from '#graphql-operations'

const props = defineProps<{
  name: string
  storeStock?: StoreStockStatusFragment
  hideToggle?: boolean
}>()

const { pickupLocations, search, userLocation, doSearch, getMyLocation, setAllocationStore, searchInput, hasSearched, allocatedStore } = useStorelocator()
const showOnlyInStock = ref(!!props.storeStock)
const storeLocationInput = ref()

const storeStockComputed = computed(() => props.storeStock)

onMounted(() => {
  watch(hasSearched, () => {
    if (hasSearched.value && showOnlyInStock.value === true && !filteredStores.value.length) {
      showOnlyInStock.value = false
    }
  })

  if (typeof window !== 'undefined' && 'navigator' in window) {
    navigator.permissions.query({ name: 'geolocation' })
      .then((permissionStatus) => {
        if (permissionStatus.state === 'granted') {
          getMyLocation()
        }
        else {
          doSearch(undefined)
        }
      })
  }
})

watch(storeStockComputed, (newVal) => {
  if (newVal && !showOnlyInStock.value) {
    showOnlyInStock.value = true
  }
})

const getStockForStore = (code: string): StoreStockStatusStoreFragment | null => {
  return props.storeStock?.stores?.find((item: StoreStockStatusStoreFragment | null) => item?.code === code) || null
}

const hasStoreStockStatus = (code: string, status: string): boolean => {
  return getStockForStore(code)?.status?.includes(status)
}

const inStockResults = computed(() => pickupLocations.value.filter(store => store.code && hasStoreStockStatus(store.code, 'in-stock')))
const backorderResults = computed(() => pickupLocations.value.filter(store => store.code && hasStoreStockStatus(store.code, 'backorder')))
const outOfStockResults = computed(() => pickupLocations.value.filter(store => store.code && hasStoreStockStatus(store.code, 'out-of-stock') && !hasStoreStockStatus(store.code, 'all-out-of-stock')))
const needToShowToggle = computed(() => inStockResults.value.length || backorderResults.value.length || outOfStockResults.value)

const filteredStores = computed(() => {
  let returnVal = []
  if (!showOnlyInStock.value) {
    returnVal = pickupLocations.value
  }
  else if (props.storeStock?.status === 'all-backorder') {
    returnVal = backorderResults.value
  }
  else if (!backorderResults.value.length && !inStockResults.value.length && !outOfStockResults.value.length) {
    returnVal = pickupLocations.value
  }
  else if (!inStockResults.value.length && !backorderResults.value.length) {
    returnVal = outOfStockResults.value
  }
  else if (!inStockResults.value.length) {
    returnVal = backorderResults.value
  }
  else {
    returnVal = inStockResults.value
  }

  return returnVal.filter(store => store.code && store.code !== allocatedStore.value?.code)
})

const storePickupDialog = useUiDialog(props.name)

const selectStore = async (store: TtLocationFragment | null) => {
  await setAllocationStore(store)
  storePickupDialog.close()
}

const formSearch = () => {
  doSearch(searchInput.value)
}
</script>

<template>
  <div>
    
<FormKitLazyProvider>
<LazyUiDialog
      :name="name"
      dialog-type="responsive"
      class="store-picker-dialog"
    >
      <template #dialogHeader>
        <span class="dialog__title">{{ $t('Choose your store') }}</span>
      </template>

      <div class="stores-pickup">
        <slot name="default" />

        <FormKit
          id="stores-pickup__search"
          name="stores-pickup__search"
          type="form"
          form-class="stores-pickup__search"
          :actions="false"
          @submit="formSearch"
        >
          <FormKit
            id="stores-pickup__search-input"
            ref="storeLocationInput"
            v-model="search"
            type="text"
            name="search"
            :label="$t('Location') + ' *'"
            :placeholder="$t('Search by postal code or place name')"
            @input="userLocation = null"
          />
          <button
            v-if="search.length > 0"
            type="button"
            class="stores-pickup__search-reset"
            @click="search = ''"
          >
            <NuxtIcon name="icon:close" />
          </button>
          <button
            type="submit"
            class="stores-pickup__search-button"
          >
            <span>{{ $t('Search locations') }}</span>
            <NuxtIcon name="icon:search" />
          </button>
          <FormKitMessages :node="storeLocationInput?.node" />
        </FormKit>

        <div class="stores-pickup__location">
          <button
            class="stores-pickup__location-btn"
            type="button"
            @click="getMyLocation"
          >
            <NuxtIcon name="icon:my-location" />
            {{ $t('Use my location') }}
          </button>
        </div>

        <div
          v-if="!props.hideToggle && props.storeStock && needToShowToggle"
          class="stores-pickup__filter"
        >
          <label class="stores-pickup__filter-label">
            <InputToggle
              v-model="showOnlyInStock"
              class="stores-pickup__filter-toggle"
            />
            <slot name="toggle">
              <span>{{ $t('Alleen winkels met voorraad') }}</span>
            </slot>
          </label>
        </div>

        <div class="stores-pickup__list">
          <StorePickerItem
            v-if="allocatedStore"
            :store="allocatedStore"
            :store-stock="storeStock"
            class="stores-pickup__item stores-pickup__item--selected"
            @select="selectStore(null)"
          />

          <StorePickerItem
            v-for="store in filteredStores"
            :key="store.code"
            :store="store"
            :store-stock="storeStock"
            class="stores-pickup__item"
            @select="selectStore"
          />
        </div>
      </div>
    </LazyUiDialog>
</FormKitLazyProvider>

  </div>
</template>

<style>
@import url("~layers/components/Store/PickerDialog.css");
</style>
